import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiUser, FiMail, FiPhone, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Modal, Button, Form } from 'react-bootstrap';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    roleIds: [],
    status: 'active',
    salary: '',
    hireDate: ''
  });
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    loadEmployees();
    loadRoles();
  }, [selectedHierarchy]);

  const loadRoles = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      const rolesQuery = query(
        collection(db, 'roles'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const snapshot = await getDocs(rolesQuery);
      const rolesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const getRoleNames = (roleIds) => {
    if (!roleIds || !Array.isArray(roleIds) || roleIds.length === 0) return '-';
    
    const roleNames = roleIds
      .map(roleId => {
        const role = roles.find(r => r.id === roleId);
        return role ? role.name : null;
      })
      .filter(Boolean);
    
    return roleNames.length > 0 ? roleNames.join(', ') : '-';
  };

  const loadEmployees = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('outletId', '==', selectedHierarchy.outlet.id));
      const snapshot = await getDocs(q);
      const employeesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error loading employees:', error);
      try {
        const employeesRef = collection(db, 'employees');
        const snapshot = await getDocs(employeesRef);
        const employeesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmployees(employeesData);
      } catch (fallbackError) {
        console.error('Fallback query failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Verify outlet is selected
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    try {
      const employeeData = {
        ...formData,
        outletId: selectedHierarchy.outlet.id,
        updatedAt: new Date()
      };

      if (editingEmployee) {
        // SECURITY: Verify the employee belongs to the current outlet
        if (editingEmployee.outletId !== selectedHierarchy.outlet.id) {
          alert('Security Error: Cannot modify employee from another outlet.');
          return;
        }
        await updateDoc(doc(db, 'employees', editingEmployee.id), employeeData);
      } else {
        employeeData.createdAt = new Date();
        await addDoc(collection(db, 'employees'), employeeData);
      }

      setShowModal(false);
      resetForm();
      loadEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Failed to save employee. Please try again.');
    }
  };

  const handleEdit = (employee) => {
    // SECURITY: Verify the employee belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (employee.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot edit employee from another outlet.');
      return;
    }
    
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      roleIds: employee.roleIds || [],
      status: employee.status || 'active',
      salary: employee.salary || '',
      hireDate: employee.hireDate || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (employee) => {
    // SECURITY: Verify the employee belongs to the current outlet
    if (!selectedHierarchy?.outlet?.id) {
      alert('Error: No outlet selected. Please select an outlet first.');
      return;
    }
    
    if (employee.outletId !== selectedHierarchy.outlet.id) {
      alert('Security Error: Cannot delete employee from another outlet.');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${employee.name}?`)) {
      try {
        await deleteDoc(doc(db, 'employees', employee.id));
        loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      roleIds: [],
      status: 'active',
      salary: '',
      hireDate: ''
    });
    setEditingEmployee(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const roleNames = getRoleNames(emp.roleIds);
    return emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      roleNames.toLowerCase().includes(searchTerm.toLowerCase());
  }).sort((a, b) => {
    let aVal, bVal;
    
    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortField === 'email') {
      aVal = (a.email || '').toLowerCase();
      bVal = (b.email || '').toLowerCase();
    } else if (sortField === 'phone') {
      aVal = (a.phone || '').toLowerCase();
      bVal = (b.phone || '').toLowerCase();
    } else if (sortField === 'role') {
      aVal = getRoleNames(a.roleIds).toLowerCase();
      bVal = getRoleNames(b.roleIds).toLowerCase();
    } else if (sortField === 'status') {
      aVal = (a.status || '').toLowerCase();
      bVal = (b.status || '').toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'danger';
      case 'on leave': return 'warning';
      default: return 'secondary';
    }
  };

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Employees</h1>
        <p className="page-subtitle">Manage your team members</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Employees</p>
                  <h3 className="mb-0">{employees.length}</h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiUser className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Active</p>
                  <h3 className="mb-0 text-success">
                    {employees.filter(emp => emp.status?.toLowerCase() === 'active').length}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-check-circle text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">On Leave</p>
                  <h3 className="mb-0 text-warning">
                    {employees.filter(emp => emp.status?.toLowerCase() === 'on leave').length}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--warning-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-calendar-x text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Departments</p>
                  <h3 className="mb-0">
                    {new Set(employees.flatMap(emp => emp.roleIds || []).filter(Boolean)).size}
                  </h3>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--accent-gradient)', width: '48px', height: '48px' }}>
                  <i className="bi bi-diagram-3 text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Employees</h5>
          <button className="btn btn-sm btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus className="me-2" />
            Add Employee
          </button>
        </div>

        {/* Filter Controls */}
        {!loading && employees.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-12">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {searchTerm && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredEmployees.length} of {employees.length} employees
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading employees...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-5">
              <FiUser size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm ? 'No employees match your search.' : 'No employees found.'}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('email')}
                    >
                      Email
                      {sortField === 'email' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('phone')}
                    >
                      Phone
                      {sortField === 'phone' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('role')}
                    >
                      Role
                      {sortField === 'role' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(employee => (
                    <tr key={employee.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: '40px',
                              height: '40px',
                              minWidth: '40px',
                              minHeight: '40px',
                              fontSize: '16px',
                              fontWeight: '600'
                            }}
                          >
                            {employee.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="fw-semibold">{employee.name}</span>
                        </div>
                      </td>
                      <td>
                        <FiMail className="text-muted me-1" size={14} />
                        {employee.email || '-'}
                      </td>
                      <td>
                        <FiPhone className="text-muted me-1" size={14} />
                        {employee.phone || '-'}
                      </td>
                      <td>
                        {(employee.roleIds && employee.roleIds.length > 0) ? (
                          <div className="d-flex flex-wrap gap-1">
                            {employee.roleIds.map(roleId => {
                              const role = roles.find(r => r.id === roleId);
                              return role ? (
                                <span key={roleId} className="badge bg-secondary">
                                  {role.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusColor(employee.status)}`}>
                          {employee.status || 'Active'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(employee)}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(employee)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Roles *</Form.Label>
                  <Form.Select
                    multiple
                    value={formData.roleIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, roleIds: selectedOptions });
                    }}
                    required
                    style={{ minHeight: '120px' }}
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Hold Ctrl (Cmd on Mac) to select multiple roles
                  </Form.Text>
                  {roles.length === 0 && (
                    <Form.Text className="text-muted d-block mt-1">
                      No roles available. Please create roles first in the Roles page.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on leave">On Leave</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Hire Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.hireDate}
                    onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-md-12">
                <Form.Group>
                  <Form.Label>Salary</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
